import {
    ApplyV2CustomFieldTypeConst,
    type ApplyV2FileAttachment,
    type ApplyV2Question,
    type ApplyV2TempBase64File,
    type ApplyV2TempCourseFile,
    type AttributesCheckboxesForm,
    type SingleSelectKey,
    type MultiSelectKey,
    type TextKey,
    type LongTextKey,
    type YesNoKey,
    type StarRatingKey,
    type DateDayKey,
    type DateMonthKey,
    type DateYearKey,
    type DateRangeStartDayKey,
    type DateRangeStartMonthKey,
    type DateRangeStartYearKey,
    type DateRangeEndDayKey,
    type DateRangeEndMonthKey,
    type DateRangeEndYearKey,
    type NumberKey,
    type AttachmentKey,
    type QualifyingKey,
    type CustomFieldKey,
    type CustomFieldDateDayKey,
    type CustomFieldDateMonthKey,
    type CustomFieldDateYearKey,
    type AttributeBoolKey,
    type AttributeCheckboxesKey,
    type AttributeDateDayKey,
    type AttributeDateMonthKey,
    type AttributeDateYearKey,
    type AttributeShortTextKey,
    type AttributeTextKey,
    type AttributeRatingKey,
    type AttributeDropdownKey,
    type ApplyV2QuestionsFormValues,
} from '@core/app/types/applyV2Type'
import ApplyV2AttachmentsUploader from '@core/components/ApplyDialogV2/ApplyV2AttachmentsUploader'
import ApplyV2DateSelectGroup from '@core/components/ApplyDialogV2/ApplyV2DateSelectGroup'
import ApplyV2InputCheckbox from '@core/components/ApplyDialogV2/ApplyV2InputCheckbox'
import ApplyV2InputRadio from '@core/components/ApplyDialogV2/ApplyV2InputRadio'
import ApplyV2InputRate from '@core/components/ApplyDialogV2/ApplyV2InputRate'
import ApplyV2InputText from '@core/components/ApplyDialogV2/ApplyV2InputText'
import ApplyV2InputTextarea from '@core/components/ApplyDialogV2/ApplyV2InputTextarea'
import ApplyV2Select from '@core/components/ApplyDialogV2/ApplyV2Select'
import FileUploaderV2 from '@core/components/ApplyDialogV2/FileUploaderV2'
import FileUploaderV2List from '@core/components/ApplyDialogV2/FileUploaderV2List'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import getAcceptFilesString from '@core/utils/helpers/getAcceptFilesString'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import ApplyV2QuestionsList from '@core/components/ApplyDialogV2/ApplyV2QuestionsStep/ApplyV2QuestionsList'
import ApplyV2InputNumber from '@core/components/ApplyDialogV2/ApplyV2InputNumber'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import WarningErrorIcon from '@core/components/icons/WarningErrorIcon'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import { useDeleteQuestionFileV2ApplyMutation, usePutQuestionV2ApplyMutation } from '@core/app/api/applyJobV2Api'
import { useFormikContext } from 'formik'
import React, { useCallback, useEffect, useRef, useState } from 'react'

interface ApplyV2QuestionsItemProps {
    question: ApplyV2Question
    setIsErrorUpdateAnswer: (value: boolean) => void
    refetchAllQuestions: () => void
    addLoadingItem: (v: string) => void
    removeLoadingItem: (v: string) => void
}

const baseClass = 'ApplyV2QuestionsStep'

function ApplyV2QuestionsItem({
    question,
    refetchAllQuestions,
    setIsErrorUpdateAnswer = (val) => {
        // console.log('setIsErrorUpdateAnswer, : ', val)
    },
    addLoadingItem = () => {},
    removeLoadingItem = () => {},
}: ApplyV2QuestionsItemProps) {
    const { onCloseApply, storageData } = useApplyV2Context()
    const { getTitle } = useApplyV2NavTranslations()
    const formik = useFormikContext<ApplyV2QuestionsFormValues>()
    const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({})
    const [loadingFilesIds, setLoadingFilesIds] = useState<(number | string)[]>([])
    const [updateAnswer, { isLoading, isError, data }] = usePutQuestionV2ApplyMutation()
    const [uploadingFilesCount, setUploadingFilesCount] = useState(0)
    const [deleteFileRequest, { isLoading: isDeleteFileLoading, error: deleteFileError }] =
        useDeleteQuestionFileV2ApplyMutation()

    useEffect(() => {
        setIsErrorUpdateAnswer(isError)
    }, [isError, setIsErrorUpdateAnswer])

    useEffect(() => {
        return () => {
            Object.values(debounceTimers.current).forEach(clearTimeout)
        }
    }, [])

    const questionKey = `${question.type}_${question.questionId}`

    const submitDebounceValue = useCallback(
        (
            field: string,
            value: any,
            options: {
                convertValueToNumber?: boolean
                setFieldValueDefer?: boolean
            } = {
                convertValueToNumber: false,
                setFieldValueDefer: false,
            }
        ) => {
            addLoadingItem(field)
            if (!options.setFieldValueDefer) {
                // console.log('setFieldValueDefer', field, value)
                formik.setSubmitting(true)
                formik.setFieldValue(field, value).then(() => {
                    console.log(formik.values)
                })
            }
            const questArr = field.split('_')
            // const questionType = questArr[0]
            const questionId = Number(questArr[questArr.length - 1])

            const fieldWithoutId = field.replace(`_${questionId}`, '')
            // console.log(field, value, fieldWithoutId)
            const dateCustomTemplate = [
                fieldWithoutId === 'year_date_customField'
                    ? value
                    : formik.values[`year_date_customField_${questionId}`],
                fieldWithoutId === 'month_date_customField'
                    ? value
                    : formik.values[`month_date_customField_${questionId}`],
                fieldWithoutId === 'day_date_customField' ? value : formik.values[`day_date_customField_${questionId}`],
            ]
            const dateSimpleTemplate = [
                fieldWithoutId === 'year_date' ? value : formik.values[`year_date_${questionId}`],
                fieldWithoutId === 'month_date' ? value : formik.values[`month_date_${questionId}`],
                fieldWithoutId === 'day_date' ? value : formik.values[`day_date_${questionId}`],
            ]
            const rangeDateTemplate = {
                start: [
                    fieldWithoutId === 'startYear_dateRange'
                        ? value
                        : formik.values[`startYear_dateRange_${questionId}`],
                    fieldWithoutId === 'startMonth_dateRange'
                        ? value
                        : formik.values[`startMonth_dateRange_${questionId}`],
                    fieldWithoutId === 'startDay_dateRange' ? value : formik.values[`startDay_dateRange_${questionId}`],
                ],
                end: [
                    fieldWithoutId === 'endYear_dateRange' ? value : formik.values[`endYear_dateRange_${questionId}`],
                    fieldWithoutId === 'endMonth_dateRange' ? value : formik.values[`endMonth_dateRange_${questionId}`],
                    fieldWithoutId === 'endDay_dateRange' ? value : formik.values[`endDay_dateRange_${questionId}`],
                ],
            }
            const attr = {
                // bool: field.includes('bool_attribute') ? value : formik.values[`bool_attribute_${questionId}`],
                bool: fieldWithoutId === 'bool_attribute' ? value : formik.values[`bool_attribute_${questionId}`],
                // rating: field.includes('rating_attribute') ? value : formik.values[`rating_attribute_${questionId}`],
                rating: field.includes('rating_attribute') ? value : formik.values[`rating_attribute_${questionId}`],
                text: fieldWithoutId === 'text_attribute' ? value : formik.values[`text_attribute_${questionId}`],
                // shortText: field.includes('shortText_attribute')
                //     ? value
                //     : formik.values[`shortText_attribute_${questionId}`],
                shortText:
                    fieldWithoutId === 'shortText_attribute'
                        ? value
                        : formik.values[`shortText_attribute_${questionId}`],
                // dropdown: field.includes('dropdowns_attribute')
                //     ? value
                //     : formik.values[`dropdowns_attribute_${questionId}`],
                dropdown:
                    fieldWithoutId === 'dropdowns_attribute'
                        ? value
                        : formik.values[`dropdowns_attribute_${questionId}`],
                // date: [
                //     field.includes('year_date_attribute') ? value : formik.values[`year_date_attribute_${questionId}`],
                //     field.includes('month_date_attribute')
                //         ? value
                //         : formik.values[`month_date_attribute_${questionId}`],
                //     field.includes('day_date_attribute') ? value : formik.values[`day_date_attribute_${questionId}`],
                // ].join('-'),
                date:
                    [
                        fieldWithoutId === 'year_date_attribute'
                            ? value
                            : formik.values[`year_date_attribute_${questionId}`],
                        fieldWithoutId === 'month_date_attribute'
                            ? value
                            : formik.values[`month_date_attribute_${questionId}`],
                        fieldWithoutId === 'day_date_attribute'
                            ? value
                            : formik.values[`day_date_attribute_${questionId}`],
                    ]
                        .filter(Boolean)
                        .join('-') || null,
                // checkbox: field.includes('checkboxes_attribute')
                //     ? (value || formik.values[`checkboxes_attribute_${questionId}`] || [])
                //           .filter(
                //               ({ id, checked, rating }: { id: number; checked: boolean; rating: number }) => checked
                //           )
                //           .map(({ id, rating }: { id: number; rating: number }) => ({ id, rating }))
                //     : [],
                checkbox:
                    fieldWithoutId === 'checkboxes_attribute'
                        ? (value || formik.values[`checkboxes_attribute_${questionId}`] || [])
                              .filter(
                                  ({ id, checked, rating }: { id: number; checked: boolean; rating: number }) => checked
                              )
                              .map(({ id, rating }: { id: number; rating: number }) => ({ id, rating }))
                        : null,
            }

            if (debounceTimers.current[field]) {
                clearTimeout(debounceTimers.current[field])
            }

            debounceTimers.current[field] = setTimeout(
                async (tims) => {
                    try {
                        if (!storageData?.postInitUid) return

                        // console.log(fieldWithoutId, questionId)
                        if (fieldWithoutId.includes('attribute')) {
                            const newTouched = { ...formik.touched } as any

                            Object.keys(formik.values).forEach((key) => {
                                if (key.includes(`attribute_${questionId}`)) {
                                    newTouched[key] = true
                                }
                            })

                            formik.setTouched(newTouched, false)

                            await updateAnswer({
                                uid: storageData.postInitUid,
                                questionId,
                                value: attr,
                            })
                                .unwrap()
                                .then((res) => {
                                    const { date } = res.data?.answer?.answer?.value ?? {}
                                    if (date) {
                                        formik.setFieldError(`year_date_attribute_${questionId}`, undefined)
                                        formik.setFieldError(`month_date_attribute_${questionId}`, undefined)
                                        formik.setFieldError(`day_date_attribute_${questionId}`, undefined)
                                    }
                                })
                                .finally(() => {
                                    // refetchAllQuestions()
                                    formik.setSubmitting(false)
                                })
                        } else if (fieldWithoutId === 'attachment') {
                            const files = Array.isArray(value) ? value : []
                            setUploadingFilesCount(files.length)

                            const uploadSequentially = async () => {
                                for (const file of files) {
                                    try {
                                        const response = await updateAnswer({
                                            uid: storageData.postInitUid,
                                            questionId,
                                            value: file,
                                        }).unwrap()

                                        if (
                                            response?.data?.answer?.answer?.value &&
                                            Array.isArray(response.data.answer.answer.value)
                                        ) {
                                            formik.setFieldValue(field, response.data.answer.answer.value)
                                        }
                                    } catch (err: any) {
                                        const errors = err?.data?.errors
                                        const res = errors.map(({ msg }: { msg: string }) => msg).join(', ')
                                        console.log('Upload error:', err?.data)
                                        if (res.length > 0) {
                                            formik.setFieldError(field, res)
                                        }
                                    } finally {
                                        setUploadingFilesCount((prev) => prev - 1)
                                    }
                                }
                            }

                            uploadSequentially().finally(() => {
                                setUploadingFilesCount(0)
                                formik.setSubmitting(false)
                                // refetchAllQuestions()
                            })
                        } else if (fieldWithoutId.includes('date_customField')) {
                            const isEverySetted = dateCustomTemplate.every((d) => !!d)
                            if (isEverySetted) {
                                await updateAnswer({
                                    uid: storageData.postInitUid,
                                    questionId,
                                    value: dateCustomTemplate.join('-'),
                                })
                                    .unwrap()
                                    .finally(() => {
                                        // refetchAllQuestions()
                                        formik.setSubmitting(false)
                                    })
                            }
                        } else if (
                            fieldWithoutId === 'year_date' ||
                            fieldWithoutId === 'month_date' ||
                            fieldWithoutId === 'day_date'
                        ) {
                            const isEverySetted = dateSimpleTemplate.every((d) => !!d)
                            // console.log(field, isEverySetted, dateSimpleTemplate)
                            if (isEverySetted) {
                                await updateAnswer({
                                    uid: storageData.postInitUid,
                                    questionId,
                                    value: dateSimpleTemplate.join('-'),
                                })
                                    .unwrap()
                                    .finally(() => {
                                        // refetchAllQuestions()
                                        formik.setSubmitting(false)
                                    })
                            }
                        } else if (fieldWithoutId.includes('dateRange')) {
                            const isEveryStartSetted = rangeDateTemplate.start.every((d) => !!d)
                            const isEveryEndSetted = rangeDateTemplate.end.every((d) => !!d)
                            // console.log(rangeDateTemplate, isEveryStartSetted, isEveryEndSetted)
                            if (isEveryStartSetted && isEveryEndSetted) {
                                await updateAnswer({
                                    uid: storageData.postInitUid,
                                    questionId,
                                    value: [rangeDateTemplate.start.join('-'), rangeDateTemplate.end.join('-')],
                                })
                                    .unwrap()
                                    .finally(() => {
                                        // refetchAllQuestions()
                                        formik.setSubmitting(false)
                                    })
                            }
                        } else {
                            await updateAnswer({
                                uid: storageData.postInitUid,
                                questionId,
                                value: options?.convertValueToNumber
                                    ? value === '' || value === null || value === undefined
                                        ? null
                                        : Number(value)
                                    : value,
                            })
                                .unwrap()
                                .finally(() => {
                                    // refetchAllQuestions()
                                    formik.setSubmitting(false)
                                })
                        }
                        // console.log('Successfully updated field:', field, value)
                    } catch (error: any) {
                        const errors = error.data?.errors ?? []

                        console.log('Failed to update answer:', errors)
                        // TODO - add statement for attributes
                        if (fieldWithoutId.includes('attribute')) {
                            const newErrors = { ...formik.errors }

                            errors.forEach((err: any) => {
                                if (err.field?.includes('bool')) {
                                    newErrors[`bool_attribute_${questionId}`] = err.msg
                                    formik.setFieldTouched(`bool_attribute_${questionId}`, true, false)
                                }
                                if (err.field?.includes('rating')) {
                                    newErrors[`rating_attribute_${questionId}`] = err.msg
                                    formik.setFieldTouched(`rating_attribute_${questionId}`, true, false)
                                }
                                if (err.field?.includes('shortText')) {
                                    newErrors[`shortText_attribute_${questionId}`] = err.msg
                                    formik.setFieldTouched(`shortText_attribute_${questionId}`, true, false)
                                }
                                if (err.field?.includes('dropdown')) {
                                    newErrors[`dropdowns_attribute_${questionId}`] = err.msg
                                    formik.setFieldTouched(`dropdowns_attribute_${questionId}`, true, false)
                                }
                                if (err.field?.includes('date')) {
                                    // Mark all date fields as touched
                                    formik.setFieldTouched(`year_date_attribute_${questionId}`, true, false)
                                    formik.setFieldTouched(`month_date_attribute_${questionId}`, true, false)
                                    formik.setFieldTouched(`day_date_attribute_${questionId}`, true, false)

                                    // Set errors
                                    newErrors[`year_date_attribute_${questionId}`] = err.msg
                                    newErrors[`month_date_attribute_${questionId}`] = err.msg
                                    newErrors[`day_date_attribute_${questionId}`] = err.msg
                                }
                            })

                            formik.setErrors(newErrors)
                        } else if (fieldWithoutId.includes('number')) {
                            const newErrors = { ...formik.errors }
                            const errorMsg =
                                errors
                                    .map((x: { msg: string[]; code: number }) => {
                                        if (x.code !== 16) {
                                            return x.msg
                                        }
                                        return [getTitle('3924', 'Required field')]
                                    })
                                    .join(', ') ?? error.message
                            formik.setFieldError(field, errorMsg)
                            formik.setFieldTouched(field, true, false)
                        } else {
                            const errorMsg = errors.map((x: any) => x.msg).join(', ') ?? error.message
                            formik.setFieldError(field, errorMsg)
                            formik.setFieldTouched(field, true, false)
                        }
                    } finally {
                        delete debounceTimers.current[field]
                        // console.log(formik.values, formik.touched, formik.errors)
                        // console.log('FIIINALLL', tims, debounceTimers.current)
                        formik.setSubmitting(false)

                        removeLoadingItem(field)
                    }
                },
                500,
                debounceTimers.current
            )
        },
        [storageData?.postInitUid, formik, debounceTimers, updateAnswer, question]
    )

    switch (question.type) {
        case 'singleSelect':
            return (
                <div className={`${baseClass}__question--item`}>
                    <ApplyV2Select
                        // id={question.questionId}
                        name={questionKey}
                        value={formik?.values[questionKey] as number | null}
                        // label={question.name}
                        label={decodeHtmlEntitiesRecursive(question.name)}
                        options={question.options.map((option: any) => ({
                            value: option.id,
                            label: option.name,
                        }))}
                        isRequired={question.required}
                        placeholder={getTitle('10293', 'Select an option')}
                        onChange={(value: number | null | string) => {
                            submitDebounceValue(questionKey, value)
                            // formik.setFieldValue(questionKey, value)
                        }}
                        onBlur={formik.handleBlur}
                        // onFocus={() => {}}
                        errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                        isSearchable={true}
                        // isLoading={isLoading}
                    />
                    <>
                        {formik?.values[questionKey] && (
                            <ApplyV2QuestionsList
                                questions={question.children}
                                // parentQuestionId={question.questionId}
                                // parentOptionId={question.parentOptionId}
                                // parentValue={formik?.values[questionKey]}
                                setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                refetchAllQuestions={refetchAllQuestions}
                                parent={{
                                    parentQuestionId: question.questionId,
                                    parentOptionId: question.parentOptionId,
                                    parentValue: formik?.values[questionKey],
                                    parentType: question.type,
                                    customFieldType: null,
                                }}
                                addLoadingItem={addLoadingItem}
                                removeLoadingItem={removeLoadingItem}
                            />
                        )}
                    </>
                </div>
            )

        case 'multiSelect':
            return (
                <div className={`${baseClass}__question--item`}>
                    <ApplyV2Select
                        // id={question.questionId}
                        name={questionKey}
                        value={formik?.values[questionKey] as (string | number)[]}
                        // label={question.name}
                        label={decodeHtmlEntitiesRecursive(question.name)}
                        options={question.options.map((option: any) => ({
                            value: option.id,
                            label: option.name,
                        }))}
                        isRequired={question.required}
                        placeholder={getTitle('10293', 'Select an option')}
                        onChange={(vals) => {
                            submitDebounceValue(questionKey, vals)
                            // formik.setFieldValue(questionKey, vals)
                        }}
                        onBlur={formik.handleBlur}
                        // onFocus={() => {}}
                        isMulti={true}
                        errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                        isSearchable={true}
                        // isLoading={isLoading}
                    />
                    {/*<ApplyV2QuestionsList questions={question.children} parentQuestionId={question.questionId} />*/}
                    {Array.isArray(formik?.values[questionKey]) &&
                        (formik?.values[questionKey] as Array<number>).length > 0 && (
                            <ApplyV2QuestionsList
                                questions={question.children}
                                // parentQuestionId={question.questionId}
                                // parentOptionId={question.parentOptionId}
                                // parentValue={formik?.values[questionKey]}
                                setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                refetchAllQuestions={refetchAllQuestions}
                                parent={{
                                    parentQuestionId: question.questionId,
                                    parentOptionId: question.parentOptionId,
                                    parentValue: formik?.values[questionKey],
                                    parentType: question.type,
                                    customFieldType: null,
                                }}
                                addLoadingItem={addLoadingItem}
                                removeLoadingItem={removeLoadingItem}
                            />
                        )}
                </div>
            )

        case 'text':
            return (
                <div className={`${baseClass}__question--item`}>
                    <ApplyV2InputText
                        // id={question.questionId}
                        name={questionKey}
                        value={formik?.values[questionKey] as string | number | boolean | undefined}
                        label={decodeHtmlEntitiesRecursive(question.name)}
                        isRequired={question.required}
                        placeholder={getTitle('10294', 'Enter your answer')}
                        onChange={(e) => {
                            const value = e.target.value
                            // formik.setFieldValue(questionKey, value)
                            submitDebounceValue(questionKey, value)
                        }}
                        onBlur={formik.handleBlur}
                        // onFocus={() => {}}
                        errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                        isLoading={isLoading}
                    />
                    {/*<ApplyV2QuestionsList questions={question.children} parentQuestionId={question.questionId} />*/}
                    <>
                        {formik?.values[questionKey] && (
                            <ApplyV2QuestionsList
                                questions={question.children}
                                // parentQuestionId={question.questionId}
                                // parentOptionId={question.parentOptionId}
                                // parentValue={formik?.values[questionKey]}
                                setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                refetchAllQuestions={refetchAllQuestions}
                                parent={{
                                    parentQuestionId: question.questionId,
                                    parentOptionId: question.parentOptionId,
                                    parentValue: formik?.values[questionKey],
                                    parentType: question.type,
                                    customFieldType: null,
                                }}
                                addLoadingItem={addLoadingItem}
                                removeLoadingItem={removeLoadingItem}
                            />
                        )}
                    </>
                </div>
            )

        case 'longText':
            return (
                <div className={`${baseClass}__question--item`}>
                    <ApplyV2InputTextarea
                        // id={question.questionId}
                        name={questionKey}
                        value={formik?.values[questionKey] as string | number | undefined}
                        label={decodeHtmlEntitiesRecursive(question.name)}
                        isRequired={question.required}
                        placeholder={getTitle('10294', 'Enter your answer')}
                        onChange={(e) => {
                            submitDebounceValue(questionKey, e.target.value)
                            // formik.setFieldValue(questionKey, e.target.value)
                        }}
                        onBlur={formik.handleBlur}
                        // onFocus={() => {}}
                        errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                        isLoading={isLoading}
                    />
                    <>
                        {formik?.values[questionKey] && (
                            <ApplyV2QuestionsList
                                questions={question.children}
                                // parentQuestionId={question.questionId}
                                // parentOptionId={question.parentOptionId}
                                // parentValue={formik?.values[questionKey]}
                                setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                refetchAllQuestions={refetchAllQuestions}
                                parent={{
                                    parentQuestionId: question.questionId,
                                    parentOptionId: question.parentOptionId,
                                    parentValue: formik?.values[questionKey],
                                    parentType: question.type,
                                    customFieldType: null,
                                }}
                                addLoadingItem={addLoadingItem}
                                removeLoadingItem={removeLoadingItem}
                            />
                        )}
                    </>
                </div>
            )

        case 'yesNo':
            return (
                <div className={`${baseClass}__question--item`}>
                    <ApplyV2InputRadio
                        // id={question.questionId}
                        name={questionKey}
                        value={formik?.values[questionKey] as number | null}
                        label={decodeHtmlEntitiesRecursive(question.name)}
                        isRequired={question.required}
                        // options={[
                        //     { value: true, label: 'Yes' },
                        //     { value: false, label: 'No' },
                        // ]}
                        options={(question.options ?? [])
                            .map((option) => ({
                                label: option.name,
                                value: option.id,
                            }))
                            .sort((a, b) => {
                                if (a.label === 'Yes') return -1
                                if (b.label === 'Yes') return 1
                                return 0
                            })}
                        onChange={(value) => {
                            submitDebounceValue(questionKey, value)
                            // formik.setFieldValue(questionKey, value)
                        }}
                        onBlur={formik.handleBlur}
                        // onFocus={() => {}}
                        errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                        // isLoading={isLoading}
                    />
                    <>
                        {formik?.values[questionKey] && (
                            <ApplyV2QuestionsList
                                questions={question.children}
                                // parentQuestionId={question.questionId}
                                // parentOptionId={question.parentOptionId}
                                // parentValue={formik?.values[questionKey]}
                                setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                refetchAllQuestions={refetchAllQuestions}
                                parent={{
                                    parentQuestionId: question.questionId,
                                    parentOptionId: question.parentOptionId,
                                    parentValue: formik?.values[questionKey],
                                    parentType: question.type,
                                    customFieldType: null,
                                }}
                                addLoadingItem={addLoadingItem}
                                removeLoadingItem={removeLoadingItem}
                            />
                        )}
                    </>
                </div>
            )

        case 'qualifying':
            return (
                <div className={`${baseClass}__question--item`}>
                    <div className={` ${baseClass}__qualifying`}>
                        <ApplyV2InputRadio
                            // id={question.questionId}
                            className={` ${baseClass}__qualifying--input`}
                            name={questionKey}
                            value={formik?.values[questionKey] as number | null}
                            label={decodeHtmlEntitiesRecursive(question.name)}
                            isRequired={question.required}
                            // options={[
                            //     { value: true, label: getTitle('128', 'Yes') },
                            //     { value: false, label: getTitle('129', 'No') },
                            // ]}
                            options={(question.options ?? [])
                                .map((option) => ({
                                    label: option.name,
                                    value: option.id,
                                }))
                                .sort((a, b) => {
                                    if (a.label === 'Yes') return -1
                                    if (b.label === 'Yes') return 1
                                    return 0
                                })}
                            onChange={(value) => {
                                // formik.setFieldValue(questionKey, value).then((errs) => {
                                submitDebounceValue(questionKey, value)
                                // })
                            }}
                            onBlur={formik.handleBlur}
                            errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                            // isLoading={isLoading}
                        />
                        {/*{formik.touched[questionKey] &&*/}
                        {formik?.values[questionKey] === question.settings.qualifyingTrigger && (
                            <div className={` ${baseClass}__qualifying--trigger`}>
                                <WarningErrorIcon className={`${baseClass}__qualifying--trigger-icon`} />
                                <div className={`${baseClass}__qualifying--trigger-message`}>
                                    {question.settings.message}
                                </div>
                                <ApplyV2Button
                                    className={`${baseClass}__qualifying--trigger-btn`}
                                    variant={'white'}
                                    size={'small'}
                                    leftIcon={<i className="fa-regular fa-xmark"></i>}
                                    onClick={onCloseApply}
                                >
                                    {getTitle('10245', 'Close application')}
                                </ApplyV2Button>
                            </div>
                        )}
                    </div>
                    <ApplyV2QuestionsList
                        questions={question.children}
                        // parentQuestionId={question.questionId}
                        // parentOptionId={question.parentOptionId}
                        // parentValue={formik?.values[questionKey]}
                        setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                        refetchAllQuestions={refetchAllQuestions}
                        parent={{
                            parentQuestionId: question.questionId,
                            parentOptionId: question.parentOptionId,
                            parentValue: formik?.values[questionKey],
                            parentType: question.type,
                            customFieldType: null,
                        }}
                        addLoadingItem={addLoadingItem}
                        removeLoadingItem={removeLoadingItem}
                    />
                </div>
            )

        case 'starRating':
            return (
                <div className={`${baseClass}__question--item`}>
                    <ApplyV2InputRate
                        // id={question.questionId}
                        name={questionKey}
                        value={formik?.values[questionKey] as number | null}
                        label={decodeHtmlEntitiesRecursive(question.name)}
                        isRequired={question.required}
                        onChange={(value) => {
                            submitDebounceValue(questionKey, value)
                            // formik.setFieldValue(questionKey, value)
                        }}
                        onBlur={formik.handleBlur}
                        // onFocus={() => {}}
                        errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                        // isLoading={isLoading}
                    />

                    {(formik?.values[questionKey] ?? 0) > 0 && (
                        <ApplyV2QuestionsList
                            questions={question.children}
                            // parentQuestionId={question.questionId}
                            // parentOptionId={question.parentOptionId}
                            // parentValue={formik?.values[questionKey]}
                            setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                            refetchAllQuestions={refetchAllQuestions}
                            parent={{
                                parentQuestionId: question.questionId,
                                parentOptionId: question.parentOptionId,
                                parentValue: formik?.values[questionKey],
                                parentType: question.type,
                                customFieldType: null,
                            }}
                            addLoadingItem={addLoadingItem}
                            removeLoadingItem={removeLoadingItem}
                        />
                    )}
                </div>
            )

        case 'date':
            return (
                <div className={`${baseClass}__question--item`}>
                    <ApplyV2DateSelectGroup
                        // id={question.questionId}
                        // name={questionKey}
                        names={{
                            day: `day_${questionKey}`,
                            month: `month_${questionKey}`,
                            year: `year_${questionKey}`,
                        }}
                        // value={parseDateToObject(formik?.values[questionKey] as string)}
                        value={{
                            day: formik?.values[`day_${questionKey}`] as string | null,
                            month: formik?.values[`month_${questionKey}`] as string | null,
                            year: formik?.values[`year_${questionKey}`] as string | null,
                        }}
                        label={decodeHtmlEntitiesRecursive(question.name)}
                        isRequired={question.required}
                        onBlur={formik.handleBlur}
                        // yearsSart={}
                        isFutureYears={false}
                        // yearsSart={1970}
                        yearsRange={80}
                        // onFocus={() => {}}
                        onChange={({ day, month, year }) => {
                            if (formik.values[`day_${questionKey}`] !== day && day !== null) {
                                submitDebounceValue(`day_${questionKey}`, day)
                            }
                            if (formik.values[`month_${questionKey}`] !== month && month !== null) {
                                submitDebounceValue(`month_${questionKey}`, month)
                            }
                            if (formik.values[`year_${questionKey}`] !== year && year !== null) {
                                submitDebounceValue(`year_${questionKey}`, year)
                            }
                        }}
                        errors={{
                            day: formik.touched[`day_${questionKey}`] && formik.errors[`day_${questionKey}`],
                            month: formik.touched[`month_${questionKey}`] && formik.errors[`month_${questionKey}`],
                            year: formik.touched[`year_${questionKey}`] && formik.errors[`year_${questionKey}`],
                        }}
                        // isLoading={isLoading}
                    />

                    <ApplyV2QuestionsList
                        questions={question.children}
                        // parentQuestionId={question.questionId}
                        // parentOptionId={question.parentOptionId}
                        // parentValue={formik?.values[questionKey]}
                        setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                        refetchAllQuestions={refetchAllQuestions}
                        parent={{
                            parentQuestionId: question.questionId,
                            parentOptionId: question.parentOptionId,
                            parentValue: formik?.values[questionKey],
                            parentType: question.type,
                            customFieldType: null,
                        }}
                        addLoadingItem={addLoadingItem}
                        removeLoadingItem={removeLoadingItem}
                    />
                </div>
            )

        case 'dateRange':
            return (
                <div className={`${baseClass}__question--item`}>
                    <div className={`${baseClass}__question--item-dateRange`}>
                        <ApplyV2DateSelectGroup
                            // id={question.questionId}
                            label={decodeHtmlEntitiesRecursive(question.name)}
                            isRequired={question.required}
                            isFutureYears={false}
                            // yearsSart={2000}
                            yearsRange={80}
                            names={{
                                day: `startDay_${questionKey}`,
                                month: `startMonth_${questionKey}`,
                                year: `startYear_${questionKey}`,
                            }}
                            value={{
                                day: formik?.values[`startDay_${questionKey}`] as string | null,
                                month: formik?.values[`startMonth_${questionKey}`] as string | null,
                                year: formik?.values[`startYear_${questionKey}`] as string | null,
                            }}
                            onChange={({ day, month, year }) => {
                                // formik.setFieldValue(`startDay_${questionKey}`, day)
                                // formik.setFieldValue(`startMonth_${questionKey}`, month)
                                // formik.setFieldValue(`startYear_${questionKey}`, year)
                                if (formik.values[`startDay_${questionKey}`] !== day && day !== null) {
                                    submitDebounceValue(`startDay_${questionKey}`, day)
                                }
                                if (formik.values[`startMonth_${questionKey}`] !== month && month !== null) {
                                    submitDebounceValue(`startMonth_${questionKey}`, month)
                                }
                                if (formik.values[`startYear_${questionKey}`] !== year && year !== null) {
                                    submitDebounceValue(`startYear_${questionKey}`, year)
                                }
                            }}
                            errors={{
                                day:
                                    formik.touched[`startDay_${questionKey}`] &&
                                    formik.errors[`startDay_${questionKey}`],
                                month:
                                    formik.touched[`startMonth_${questionKey}`] &&
                                    formik.errors[`startMonth_${questionKey}`],
                                year:
                                    formik.touched[`startYear_${questionKey}`] &&
                                    formik.errors[`startYear_${questionKey}`],
                            }}
                            onBlur={formik.handleBlur}
                            // isLoading={isLoading}
                        />
                        <ApplyV2DateSelectGroup
                            // id={question.questionId}
                            isRequired={question.required}
                            isFutureYears={false}
                            yearsRange={80}
                            yearsSart={formik?.values[`startYear_${questionKey}`] as string | null}
                            names={{
                                day: `endDay_${questionKey}`,
                                month: `endMonth_${questionKey}`,
                                year: `endYear_${questionKey}`,
                            }}
                            value={{
                                day: formik?.values[`endDay_${questionKey}`] as string | null,
                                month: formik?.values[`endMonth_${questionKey}`] as string | null,
                                year: formik?.values[`endYear_${questionKey}`] as string | null,
                            }}
                            onChange={({ day, month, year }) => {
                                // formik.setFieldValue(`endDay_${questionKey}`, day)
                                // formik.setFieldValue(`endMonth_${questionKey}`, month)
                                // formik.setFieldValue(`endYear_${questionKey}`, year)
                                if (formik.values[`endDay_${questionKey}`] !== day && day !== null) {
                                    submitDebounceValue(`endDay_${questionKey}`, day)
                                }
                                if (formik.values[`endMonth_${questionKey}`] !== month && month !== null) {
                                    submitDebounceValue(`endMonth_${questionKey}`, month)
                                }
                                if (formik.values[`endYear_${questionKey}`] !== year && year !== null) {
                                    submitDebounceValue(`endYear_${questionKey}`, year)
                                }
                            }}
                            errors={{
                                day: formik.touched[`endDay_${questionKey}`] && formik.errors[`endDay_${questionKey}`],
                                month:
                                    formik.touched[`endMonth_${questionKey}`] &&
                                    formik.errors[`endMonth_${questionKey}`],
                                year:
                                    formik.touched[`endYear_${questionKey}`] && formik.errors[`endYear_${questionKey}`],
                            }}
                            onBlur={formik.handleBlur}
                            // isLoading={isLoading}
                        />
                    </div>
                    <ApplyV2QuestionsList
                        questions={question.children}
                        // parentQuestionId={question.questionId}
                        // parentOptionId={question.parentOptionId}
                        // parentValue={formik?.values[questionKey]}
                        setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                        refetchAllQuestions={refetchAllQuestions}
                        parent={{
                            parentQuestionId: question.questionId,
                            parentOptionId: question.parentOptionId,
                            parentValue: formik?.values[questionKey],
                            parentType: question.type,
                            customFieldType: null,
                        }}
                        addLoadingItem={addLoadingItem}
                        removeLoadingItem={removeLoadingItem}
                    />
                </div>
            )

        case 'number':
            return (
                <div className={`${baseClass}__question--item`}>
                    <ApplyV2InputNumber
                        name={questionKey}
                        value={formik?.values[questionKey] as number | undefined | null}
                        label={decodeHtmlEntitiesRecursive(question.name)}
                        isRequired={question.required}
                        placeholder={getTitle('10294', 'Enter your answer')}
                        onChange={(e) => {
                            submitDebounceValue(questionKey, e, {
                                convertValueToNumber: true,
                            })
                        }}
                        onBlur={formik.handleBlur}
                        errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                        isLoading={isLoading}
                    />
                    <>
                        {formik?.values[questionKey] && (
                            <ApplyV2QuestionsList
                                questions={question.children}
                                // parentQuestionId={question.questionId}
                                // parentOptionId={question.parentOptionId}
                                // parentValue={formik?.values[questionKey]}
                                setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                refetchAllQuestions={refetchAllQuestions}
                                parent={{
                                    parentQuestionId: question.questionId,
                                    parentOptionId: question.parentOptionId,
                                    parentValue: formik?.values[questionKey],
                                    parentType: question.type,
                                    customFieldType: null,
                                }}
                                addLoadingItem={addLoadingItem}
                                removeLoadingItem={removeLoadingItem}
                            />
                        )}
                    </>
                </div>
            )

        case 'attachment':
            const files =
                (formik?.values[questionKey] as (
                    | ApplyV2TempBase64File
                    | ApplyV2FileAttachment
                    | ApplyV2TempCourseFile
                )[]) ?? []
            const isHideInput = question.settings?.multiple ? files.length >= 5 : files.length >= 1
            // console.log(isHideInput, 'isHideInput', files.length)
            return (
                <div className={`${baseClass}__question--item`}>
                    <ApplyV2AttachmentsUploader
                        name={questionKey}
                        label={decodeHtmlEntitiesRecursive(question.name)}
                        isRequired={question.required}
                        multipleCount={question.settings?.multiple ? 5 : 1}
                        isShowInput={!isHideInput}
                        onChange={(files) => {
                            submitDebounceValue(questionKey, files, { setFieldValueDefer: true })
                            // formik.setFieldValue(questionKey, processedFiles)
                        }}
                        maxFileSize={30}
                        accept={getAcceptFilesString('ATTACHMENTS')}
                        files={files}
                        onBlur={(e) => {
                            // console.log('On Blor here', e)
                            // formik.validateField(questionKey)
                            formik.handleBlur(e)
                        }}
                        onDelete={(f) => {
                            // const files = (formik.values[questionKey] as ApplyV2FileAttachment[]) ?? []
                            // submitDebounceValue(
                            //     questionKey,
                            //     files.filter((it) => it.name !== f.name)
                            // )
                            console.log('Delete', f)
                            if (!('uid' in f)) {
                                console.error('File does not have a UID and cannot be deleted.')
                                return
                            }
                            setLoadingFilesIds((prev) => {
                                return [...prev, f.uid]
                            })
                            const files = (formik.values[questionKey] as ApplyV2FileAttachment[]) ?? []
                            formik.setFieldValue(
                                questionKey,
                                files.filter((fff) => fff.uid !== f.uid)
                            )
                            deleteFileRequest({
                                uid: storageData?.postInitUid,
                                questionId: question.questionId,
                                fileUid: f.uid,
                            })
                                .unwrap()
                                .then((res) => {
                                    // const files = (formik.values[questionKey] as ApplyV2FileAttachment[]) ?? []
                                    // formik.setFieldValue(
                                    //     questionKey,
                                    //     files.filter((fff) => fff.uid !== f.uid)
                                    // )
                                })
                                .catch((err) => {
                                    console.log(err)
                                })
                                .finally(() => {
                                    setLoadingFilesIds((prev) => {
                                        return prev.filter((fi) => fi !== f.uid)
                                    })
                                })
                        }}
                        isLoading={isLoading}
                        loadingIds={loadingFilesIds}
                        uploadingCount={uploadingFilesCount}
                        errorMessage={
                            formik.touched[questionKey] &&
                            formik.errors[questionKey] &&
                            JSON.stringify(formik.errors[questionKey])
                        }
                    />

                    {Array.isArray(formik?.values[questionKey]) &&
                        (formik?.values[questionKey] as Array<any>).length > 0 && (
                            <ApplyV2QuestionsList
                                questions={question.children}
                                // parentQuestionId={question.questionId}
                                // parentOptionId={question.parentOptionId}
                                // parentValue={formik?.values[questionKey]}
                                setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                refetchAllQuestions={refetchAllQuestions}
                                parent={{
                                    parentQuestionId: question.questionId,
                                    parentOptionId: question.parentOptionId,
                                    parentValue: formik?.values[questionKey],
                                    parentType: question.type,
                                    customFieldType: null,
                                }}
                                addLoadingItem={addLoadingItem}
                                removeLoadingItem={removeLoadingItem}
                            />
                        )}
                </div>
            )

        case 'customField':
            switch (question.customField.typeId) {
                case ApplyV2CustomFieldTypeConst.SingleSelect:
                    return (
                        <div className={`${baseClass}__question--item`}>
                            <ApplyV2Select
                                // id={question.questionId}
                                name={questionKey}
                                value={formik?.values[questionKey] as number | null}
                                label={decodeHtmlEntitiesRecursive(question.name)}
                                options={(question.options ?? []).map((option: any) => ({
                                    value: option.id,
                                    label: option.name,
                                }))}
                                isRequired={question.required}
                                placeholder={getTitle('10293', 'Select an option')}
                                onChange={(value: number | null | string) => {
                                    submitDebounceValue(questionKey, value)
                                    // formik.setFieldValue(questionKey, value)
                                }}
                                errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                                onBlur={formik.handleBlur}
                                description={question.customField.description}
                                isSearchable={true}
                                // isLoading={isLoading}
                            />
                            {/*{question.customField.description && <div>{question.customField.description}</div>}*/}

                            <>
                                {formik?.values[questionKey] && (
                                    <ApplyV2QuestionsList
                                        questions={question.children}
                                        // parentQuestionId={question.questionId}
                                        // parentOptionId={question.parentOptionId}
                                        // parentValue={formik?.values[questionKey]}
                                        setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                        refetchAllQuestions={refetchAllQuestions}
                                        parent={{
                                            parentQuestionId: question.questionId,
                                            parentOptionId: question.parentOptionId,
                                            parentValue: formik?.values[questionKey],
                                            parentType: question.type,
                                            customFieldType: question.customField?.typeId,
                                        }}
                                        addLoadingItem={addLoadingItem}
                                        removeLoadingItem={removeLoadingItem}
                                    />
                                )}
                            </>
                        </div>
                    )
                case ApplyV2CustomFieldTypeConst.MultiSelect:
                    return (
                        <div className={`${baseClass}__question--item`}>
                            <ApplyV2Select
                                // id={question.questionId}
                                name={questionKey}
                                value={formik?.values[questionKey] as (string | number)[]}
                                label={decodeHtmlEntitiesRecursive(question.name)}
                                options={(question.options ?? []).map((option: any) => ({
                                    value: option.id,
                                    label: option.name,
                                }))}
                                isRequired={question.required}
                                placeholder={getTitle('10293', 'Select an option')}
                                onChange={(vals) => {
                                    submitDebounceValue(questionKey, vals)
                                    // formik.setFieldValue(questionKey, vals)
                                }}
                                // onFocus={() => {}}
                                isMulti={true}
                                onBlur={formik.handleBlur}
                                errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                                description={question.customField.description}
                                isSearchable={true}
                                // isLoading={isLoading}
                            />
                            {/*{question.customField.description && <div>{question.customField.description}</div>}*/}

                            {Array.isArray(formik?.values[questionKey]) &&
                                (formik?.values[questionKey] as Array<any>).length > 0 && (
                                    <ApplyV2QuestionsList
                                        questions={question.children}
                                        // parentQuestionId={question.questionId}
                                        // parentOptionId={question.parentOptionId}
                                        // parentValue={formik?.values[questionKey]}
                                        setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                        refetchAllQuestions={refetchAllQuestions}
                                        parent={{
                                            parentQuestionId: question.questionId,
                                            parentOptionId: question.parentOptionId,
                                            parentValue: formik?.values[questionKey],
                                            parentType: question.type,
                                            customFieldType: question.customField?.typeId,
                                        }}
                                        addLoadingItem={addLoadingItem}
                                        removeLoadingItem={removeLoadingItem}
                                    />
                                )}
                        </div>
                    )
                case ApplyV2CustomFieldTypeConst.Text:
                    return (
                        <div className={`${baseClass}__question--item`}>
                            <ApplyV2InputText
                                // id={question.questionId}
                                name={questionKey}
                                value={formik?.values[questionKey] as string | number | boolean | undefined}
                                label={decodeHtmlEntitiesRecursive(question.name)}
                                isRequired={question.required}
                                placeholder={getTitle('10294', 'Enter your answer')}
                                onChange={(e) => {
                                    submitDebounceValue(questionKey, e.target.value)
                                    // formik.setFieldValue(questionKey, e.target.value)
                                }}
                                onBlur={formik.handleBlur}
                                errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                                description={question.customField.description}
                                isLoading={isLoading}
                            />
                            {/*{question.customField.description && <div>{question.customField.description}</div>}*/}

                            <>
                                {formik?.values[questionKey] && (
                                    <ApplyV2QuestionsList
                                        questions={question.children}
                                        // parentQuestionId={question.questionId}
                                        // parentOptionId={question.parentOptionId}
                                        // parentValue={formik?.values[questionKey]}
                                        setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                        refetchAllQuestions={refetchAllQuestions}
                                        parent={{
                                            parentQuestionId: question.questionId,
                                            parentOptionId: question.parentOptionId,
                                            parentValue: formik?.values[questionKey],
                                            parentType: question.type,
                                            customFieldType: question.customField?.typeId,
                                        }}
                                        addLoadingItem={addLoadingItem}
                                        removeLoadingItem={removeLoadingItem}
                                    />
                                )}
                            </>
                        </div>
                    )
                case ApplyV2CustomFieldTypeConst.Integer:
                    return (
                        <div className={`${baseClass}__question--item`}>
                            <ApplyV2InputNumber
                                // id={question.questionId}
                                name={questionKey}
                                value={formik?.values[questionKey] as string | number}
                                label={decodeHtmlEntitiesRecursive(question.name)}
                                isRequired={question.required}
                                placeholder={getTitle('10294', 'Enter your answer')}
                                min={question.customField.properties?.min}
                                max={question.customField.properties?.max}
                                decimals={question.customField.properties?.decimals}
                                onChange={(value) => {
                                    submitDebounceValue(questionKey, value, { convertValueToNumber: true })
                                    // formik.setFieldValue(questionKey, value)
                                }}
                                onBlur={formik.handleBlur}
                                errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                                currencyId={question.customField.properties?.currencyId}
                                description={question.customField.description}
                                isLoading={isLoading}
                                numberFormat={
                                    question.customField.properties?.numberFormat === 2 ? 'percent' : 'default'
                                }
                            />
                            {/*{question.customField.description && <div>{question.customField.description}</div>}*/}

                            <>
                                {formik?.values[questionKey] && (
                                    <ApplyV2QuestionsList
                                        questions={question.children}
                                        // parentQuestionId={question.questionId}
                                        // parentOptionId={question.parentOptionId}
                                        // parentValue={formik?.values[questionKey]}
                                        setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                        refetchAllQuestions={refetchAllQuestions}
                                        parent={{
                                            parentQuestionId: question.questionId,
                                            parentOptionId: question.parentOptionId,
                                            parentValue: formik?.values[questionKey],
                                            parentType: question.type,
                                            customFieldType: question.customField?.typeId,
                                        }}
                                        addLoadingItem={addLoadingItem}
                                        removeLoadingItem={removeLoadingItem}
                                    />
                                )}
                            </>
                        </div>
                    )
                case ApplyV2CustomFieldTypeConst.Date:
                    return (
                        <div className={`${baseClass}__question--item`}>
                            <ApplyV2DateSelectGroup
                                // id={question.questionId}
                                // name={questionKey}
                                names={{
                                    day: `day_date_${questionKey}`,
                                    month: `month_date_${questionKey}`,
                                    year: `year_date_${questionKey}`,
                                }}
                                // value={parseDateToObject(formik?.values[questionKey] as string)}
                                value={{
                                    day: formik?.values[`day_date_${questionKey}`] as string | null,
                                    month: formik?.values[`month_date_${questionKey}`] as string | null,
                                    year: formik?.values[`year_date_${questionKey}`] as string | null,
                                }}
                                isFutureYears={false}
                                yearsRange={80}
                                // yearsSart={1980}
                                label={decodeHtmlEntitiesRecursive(question.name)}
                                isRequired={question.required}
                                // onBlur={formik.validateOnBlur}
                                // onFocus={() => {}}
                                onChange={({ day, month, year }) => {
                                    // formik.setFieldValue(`day_date_${questionKey}`, day)
                                    // formik.setFieldValue(`month_date_${questionKey}`, month)
                                    // formik.setFieldValue(`year_date_${questionKey}`, year)
                                    if (formik.values[`day_date_${questionKey}`] !== day && day !== null) {
                                        submitDebounceValue(`day_date_${questionKey}`, day)
                                    }
                                    if (formik.values[`month_date_${questionKey}`] !== month && month !== null) {
                                        submitDebounceValue(`month_date_${questionKey}`, month)
                                    }
                                    if (formik.values[`year_date_${questionKey}`] !== year && year !== null) {
                                        submitDebounceValue(`year_date_${questionKey}`, year)
                                    }
                                }}
                                errors={{
                                    day:
                                        formik.touched[`day_date_${questionKey}`] &&
                                        formik.errors[`day_date_${questionKey}`],
                                    month:
                                        formik.touched[`month_date_${questionKey}`] &&
                                        formik.errors[`month_date_${questionKey}`],
                                    year:
                                        formik.touched[`year_date_${questionKey}`] &&
                                        formik.errors[`year_date_${questionKey}`],
                                }}
                                onBlur={formik.handleBlur}
                                description={question.customField.description}
                                // isLoading={isLoading}
                            />
                            {/*{question.customField.description && <div>{question.customField.description}</div>}*/}
                            <ApplyV2QuestionsList
                                questions={question.children}
                                // parentQuestionId={question.questionId}
                                // parentOptionId={question.parentOptionId}
                                // parentValue={formik?.values[questionKey]}
                                setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                refetchAllQuestions={refetchAllQuestions}
                                parent={{
                                    parentQuestionId: question.questionId,
                                    parentOptionId: question.parentOptionId,
                                    parentValue: formik?.values[questionKey],
                                    parentType: question.type,
                                    customFieldType: question.customField?.typeId,
                                }}
                                addLoadingItem={addLoadingItem}
                                removeLoadingItem={removeLoadingItem}
                            />
                        </div>
                    )
                default:
                    return (
                        <div className={`${baseClass}__question--item`}>
                            <ApplyV2InputText
                                // id={question.questionId}
                                name={questionKey}
                                value={formik?.values[questionKey] as string | number | boolean | undefined}
                                label={decodeHtmlEntitiesRecursive(question.name)}
                                isRequired={question.required}
                                placeholder={getTitle('10294', 'Enter your answer')}
                                onChange={(e) => {
                                    submitDebounceValue(questionKey, e.target.value)
                                    // formik.setFieldValue(questionKey, e.target.value)
                                }}
                                onBlur={formik.handleBlur}
                                // onFocus={() => {}}
                                errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                                isLoading={isLoading}
                            />
                            {/*{question.customField.description && <div>{question.customField.description}</div>}*/}

                            <>
                                {formik?.values[questionKey] && (
                                    <ApplyV2QuestionsList
                                        questions={question.children}
                                        // parentQuestionId={question.questionId}
                                        // parentOptionId={question.parentOptionId}
                                        // parentValue={formik?.values[questionKey]}
                                        setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                        refetchAllQuestions={refetchAllQuestions}
                                        parent={{
                                            parentQuestionId: question.questionId,
                                            parentOptionId: question.parentOptionId,
                                            parentValue: formik?.values[questionKey],
                                            parentType: question.type,
                                            customFieldType: null,
                                        }}
                                        addLoadingItem={addLoadingItem}
                                        removeLoadingItem={removeLoadingItem}
                                    />
                                )}
                            </>
                        </div>
                    )
            }

        case 'attribute':
            return (
                <div className={`${baseClass}__question--item ${baseClass}__attribute`}>
                    <div className={`${baseClass}__attribute--title`}>
                        {decodeHtmlEntitiesRecursive(question.name)}
                        {question.required && '*'}
                    </div>

                    {question.attribute.bool && (
                        <ApplyV2InputRadio
                            // id={`${question.questionId}--bool`}
                            name={`bool_${questionKey}`}
                            // className={`${baseClass}__question--item`}
                            className={`${baseClass}__attribute--data`}
                            value={formik?.values[`bool_${questionKey}`] as number | null}
                            options={[
                                { value: true, label: getTitle('128', 'Yes') },
                                { value: false, label: getTitle('129', 'No') },
                            ]}
                            onChange={(value) => {
                                submitDebounceValue(`bool_${questionKey}`, value)
                                // formik.setFieldValue(`bool_${questionKey}`, value)
                            }}
                            isRequired={question.required}
                            onBlur={formik.handleBlur}
                            // onFocus={() => {}}
                            errorMessage={formik.touched[`bool_${questionKey}`] && formik.errors[`bool_${questionKey}`]}
                            // isLoading={isLoading}
                        />
                    )}

                    {question.attribute.dropdown && question.attribute.dropdowns && (
                        <ApplyV2Select
                            // id={`${question.questionId}--dropdown`}
                            name={`dropdowns_${questionKey}`}
                            className={`${baseClass}__attribute--data`}
                            value={formik?.values[`dropdowns_${questionKey}`] as number | null}
                            options={question.attribute.dropdowns.map((d: any) => ({
                                value: d.id,
                                label: d.name,
                            }))}
                            isRequired={question.required}
                            placeholder={getTitle('10293', 'Select an option')}
                            onChange={(value: number | null | string) => {
                                submitDebounceValue(`dropdowns_${questionKey}`, value)
                                // formik.setFieldValue(`dropdowns_${questionKey}`, value)
                            }}
                            onBlur={formik.handleBlur}
                            // onFocus={() => {}}
                            errorMessage={
                                formik.touched[`dropdowns_${questionKey}`] && formik.errors[`dropdowns_${questionKey}`]
                            }
                            isSearchable={true}
                            // isLoading={isLoading}
                        />
                    )}

                    {question.attribute.shortText && (
                        <ApplyV2InputText
                            // id={`${question.questionId}--shortText`}
                            name={`shortText_${questionKey}`}
                            className={`${baseClass}__attribute--data`}
                            value={formik?.values[`shortText_${questionKey}`] as string}
                            placeholder={getTitle('10294', 'Enter your answer')}
                            isRequired={question.required}
                            onChange={(e) => {
                                submitDebounceValue(`shortText_${questionKey}`, e.target.value)
                                // formik.setFieldValue(`shortText_${questionKey}`, e.target.value)
                            }}
                            onBlur={formik.handleBlur}
                            // onFocus={() => {}}
                            errorMessage={
                                formik.touched[`shortText_${questionKey}`] && formik.errors[`shortText_${questionKey}`]
                            }
                            // isLoading={isLoading}
                        />
                    )}

                    {question.attribute.rating && (
                        <ApplyV2InputRate
                            // id={`${question.questionId}--rating`}
                            className={`${baseClass}__attribute--data`}
                            name={`rating_${questionKey}`}
                            value={formik?.values[`rating_${questionKey}`] as number | null}
                            onChange={(e) => {
                                submitDebounceValue(`rating_${questionKey}`, e)
                                // formik.setFieldValue(`rating_${questionKey}`, e)
                            }}
                            onBlur={formik.handleBlur}
                            errorMessage={
                                formik.touched[`rating_${questionKey}`] && formik.errors[`rating_${questionKey}`]
                            }
                            // isLoading={isLoading}
                        />
                    )}
                    {question.attribute.text && (
                        <ApplyV2InputTextarea
                            // id={`${question.questionId}--text`}
                            className={`${baseClass}__attribute--data`}
                            name={`text_${questionKey}`}
                            value={formik?.values[`text_${questionKey}`] as string}
                            isRequired={question.required}
                            placeholder={getTitle('10294', 'Enter your answer')}
                            onChange={(e) => {
                                submitDebounceValue(`text_${questionKey}`, e.target.value)
                                // formik.setFieldValue(`text_${questionKey}`, e.target.value)
                            }}
                            onBlur={formik.handleBlur}
                            // onFocus={() => {}}
                            errorMessage={formik.touched[`text_${questionKey}`] && formik.errors[`text_${questionKey}`]}
                            // isLoading={isLoading}
                        />
                    )}

                    {question.attribute.date && (
                        <ApplyV2DateSelectGroup
                            // id={`${question.questionId}--date`}
                            className={`${baseClass}__attribute--data`}
                            names={{
                                day: `day_date_${questionKey}`,
                                month: `month_date_${questionKey}`,
                                year: `year_date_${questionKey}`,
                            }}
                            value={{
                                day: formik?.values[`day_date_${questionKey}`] as string | null,
                                month: formik?.values[`month_date_${questionKey}`] as string,
                                year: formik?.values[`year_date_${questionKey}`] as string,
                            }}
                            isRequired={question.required}
                            isFutureYears={false}
                            yearsRange={60}
                            yearsSart={2030}
                            onBlur={formik.handleBlur}
                            // onFocus={() => {}}
                            onChange={({ day, month, year }) => {
                                // formik.setFieldValue(`day_date_${questionKey}`, day)
                                // formik.setFieldValue(`month_date_${questionKey}`, month)
                                // formik.setFieldValue(`year_date_${questionKey}`, year)
                                if (formik.values[`day_date_${questionKey}`] !== day && day !== null) {
                                    // console.log(formik.values[`day_date_${questionKey}`], day)
                                    submitDebounceValue(`day_date_${questionKey}`, day)
                                }
                                if (formik.values[`month_date_${questionKey}`] !== month && month !== null) {
                                    // console.log(formik.values[`month_date_${questionKey}`], month)
                                    submitDebounceValue(`month_date_${questionKey}`, month)
                                }
                                if (formik.values[`year_date_${questionKey}`] !== year && year !== null) {
                                    // console.log(formik.values[`year_date_${questionKey}`], year)
                                    submitDebounceValue(`year_date_${questionKey}`, year)
                                }
                            }}
                            errors={{
                                day:
                                    formik.touched[`day_date_${questionKey}`] &&
                                    formik.errors[`day_date_${questionKey}`],
                                month:
                                    formik.touched[`month_date_${questionKey}`] &&
                                    formik.errors[`month_date_${questionKey}`],
                                year:
                                    formik.touched[`year_date_${questionKey}`] &&
                                    formik.errors[`year_date_${questionKey}`],
                            }}
                            // isLoading={isLoading}
                        />
                    )}

                    {question.attribute.checkbox && question.attribute.checkboxes && (
                        <div>
                            <div className={`${baseClass}__attribute--data ${baseClass}__attribute--data-checkboxes`}>
                                {question.attribute.checkboxes.map((checkbox) => {
                                    return (
                                        <div
                                            key={checkbox.id}
                                            className={` ${baseClass}__attribute--data-checkboxes-item`}
                                        >
                                            <ApplyV2InputCheckbox
                                                // id={checkbox.id}\
                                                className={` ${baseClass}__attribute--data-checkboxes-item-input`}
                                                name={`checkboxes_${questionKey}`}
                                                value={
                                                    (Array.isArray(formik?.values[`checkboxes_${questionKey}`])
                                                        ? (formik.values[
                                                              `checkboxes_${questionKey}`
                                                          ] as AttributesCheckboxesForm[])
                                                        : []
                                                    ).find((it) => it.id === checkbox.id)?.checked
                                                }
                                                label={decodeHtmlEntitiesRecursive(checkbox.name)}
                                                onChange={(value) => {
                                                    const currentValues = formik.values[`checkboxes_${questionKey}`]
                                                    const checkboxesArray = Array.isArray(currentValues)
                                                        ? (currentValues as AttributesCheckboxesForm[])
                                                        : []

                                                    const values = checkboxesArray.map((it) => {
                                                        if (it.id === checkbox.id)
                                                            return {
                                                                ...it,
                                                                checked: value,
                                                                rating: !value ? 0 : it.rating,
                                                            }
                                                        return it
                                                    })
                                                    // formik.setFieldValue(`checkboxes_${questionKey}`, values)
                                                    submitDebounceValue(`checkboxes_${questionKey}`, values)
                                                }}
                                                onBlur={formik.handleBlur}
                                                errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                                                // isLoading={isLoading}
                                            />
                                            {question.attribute.checkboxRating && (
                                                <ApplyV2InputRate
                                                    className={` ${baseClass}__attribute--data-checkboxes-item-rating`}
                                                    name={`checkboxes_${questionKey}`}
                                                    value={
                                                        (Array.isArray(formik?.values[`checkboxes_${questionKey}`])
                                                            ? (formik.values[
                                                                  `checkboxes_${questionKey}`
                                                              ] as AttributesCheckboxesForm[])
                                                            : []
                                                        ).find((it) => it.id === checkbox.id)?.rating
                                                    }
                                                    onChange={(value) => {
                                                        const currentValues = formik.values[`checkboxes_${questionKey}`]
                                                        const checkboxesArray = Array.isArray(currentValues)
                                                            ? (currentValues as AttributesCheckboxesForm[])
                                                            : []
                                                        const values = checkboxesArray.map((it) => {
                                                            if (it.id === checkbox.id)
                                                                return { ...it, rating: value, checked: true }
                                                            return it
                                                        })
                                                        // formik.setFieldValue(`checkboxes_${questionKey}`, values)
                                                        submitDebounceValue(`checkboxes_${questionKey}`, values)
                                                    }}
                                                    // isDisabled={
                                                    //     (Array.isArray(formik?.values[`checkboxes_${questionKey}`])
                                                    //         ? (formik.values[
                                                    //               `checkboxes_${questionKey}`
                                                    //           ] as AttributesCheckboxesForm[])
                                                    //         : []
                                                    //     ).find((it) => it.id === checkbox.id)?.checked !== true
                                                    // }
                                                    onBlur={formik.handleBlur}
                                                    // isLoading={isLoading}
                                                />
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                            {formik.touched[`checkboxes_${questionKey}`] &&
                                formik.errors[`checkboxes_${questionKey}`] && (
                                    <div className={`${baseClass}__attribute__error`}>
                                        <WarningErrorIcon className={`${baseClass}__attribute__error--icon`} />
                                        <div className={`${baseClass}__attribute__error--text`}>
                                            {formik.errors[`checkboxes_${questionKey}`]}
                                        </div>
                                    </div>
                                )}
                        </div>
                    )}
                </div>
            )

        default:
            return null
    }
}

export default ApplyV2QuestionsItem
